import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LeaderboardTable from './LeaderboardTable'
import ShareButton from './ShareButton'
import TournamentBracketsTab from './TournamentBracketsTab'
import TournamentHero from './TournamentHero'
import TournamentPrizes from './TournamentPrizes'
import { Card, CardContent, CardHeader } from './ui/card'
import { SwipeableTabsContent, Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { useNotification } from '../contexts/NotificationContext'
import { useUser } from '../contexts/UserContext'
import { compareDivisions } from '../lib/divisionOrder'
import { renderLeaderboardShareCard, SHARE_CARD_VARIANTS } from '../lib/shareCard'
import { getShareUrl } from '../lib/shareUrl'
import {
  fetchLeaderboardRankings,
  fetchTournament as fetchTournamentService,
  fetchUserBracketEntries,
  fetchUserBracketStats,
} from '../services/tournamentService'
import { getEntries } from '../services/userService'

/**
 * The Brackets / Leaderboard / Prizes experience for a single tournament,
 * self-fetching by tournamentId. Extracted from TournamentView so it can be
 * reused both as the full /tournaments/:id page body and embedded inline under
 * "My Tournaments" on Home (owner request 2026-06-03).
 *
 * Props:
 *  - tournamentId: which tournament to load
 *  - showHero: render the TournamentHero header. The Home embed hides it
 *    because ProfileBody already shows the tournament name + status above it.
 *  - swipeable: use the swipe-between-tabs container. On by default for the
 *    full-page view. The Home embed turns it OFF: SwipeableTabsContent
 *    force-mounts the adjacent panels (so a swipe can reveal them), which
 *    makes the panel as tall as its tallest neighbor — a short Brackets tab
 *    next to a long Leaderboard then shows a big empty gap. Plain tabs mount
 *    only the active panel, so the height hugs the content.
 *  - groupId / groupBracketIds: the group page's tournament card (owner
 *    2026-10-07: "same layout as the home page, only the group's members").
 *    Brackets narrow to the ones in the group, and the Leaderboard and Prizes
 *    tabs to the group.
 *  - champUserIds / champNames: the group's dub Champ(s) once the tournament
 *    has ended, crowned on the leaderboard and named on Bragging Rights.
 */
export default function TournamentDetailPanel({
  tournamentId,
  showHero = true,
  swipeable = true,
  userId = null,
  providedEntries = null,
  groupId = null,
  groupBracketIds = null,
  champUserIds = null,
  champNames = [],
}) {
  const { user } = useUser()
  // DUB-174: when userId targets another player's profile, show THEIR entered
  // brackets plus the shared tournament Leaderboard/Prizes inline — instead of
  // a bare link that navigated away. Leaderboard/Prizes are tournament-scoped
  // (same for everyone); only the Brackets tab is per-user.
  //
  // providedEntries: the profile owner's entries, already fetched through the
  // gated get_entries RPC by ProfileBody. We MUST use these for another user
  // rather than self-fetching: fetchUserBracketEntries hits bracket_entries
  // directly, and RLS hides another user's entries — which left the Brackets
  // tab empty ("No brackets available yet") on other people's profiles.
  const targetUserId = userId || user?.id
  const viewerIsOwner = !userId || userId === user?.id
  const navigate = useNavigate()
  const notify = useNotification()

  // DUB-174 #1: clicking a name on a TOURNAMENT leaderboard jumps straight to
  // that player's bracket for THIS tournament — single-bracket goes to their
  // entry's picks; a multi-division tournament opens their profile with this
  // tournament pre-expanded so they can choose a division. (The global
  // leaderboard keeps the default profile navigation.)
  const handleLeaderboardUserClick = async (row) => {
    const isSelf = row.user_id === user?.id
    try {
      const list = (await getEntries({ p_user_id: row.user_id, p_tournament_id: tournamentId })) || []
      // Owner rule (DUB-150/DUB-197): nobody can view another player's picks
      // until that bracket locks. Block the navigation here outright — the
      // picks page would only bounce the entry anyway, which reads as a
      // confusing blank bracket.
      const viewable = isSelf
        ? list
        : list.filter(e => {
            const b = brackets.find(br => br.id === e.bracket_id)
            if (!b) return false
            const picksArePublic = b.rounds?.length
              ? b.rounds.some(r => r.status === 'locked' || r.status === 'complete')
              : ['locked', 'complete'].includes(b.status)
            return picksArePublic
          })
      if (!isSelf && list.length > 0 && viewable.length === 0) {
        notify.warning("You can view other players' picks once the bracket locks.")
        return
      }
      if (viewable.length === 1) {
        navigate(`/bracket/${viewable[0].bracket_id}/picks?entryId=${viewable[0].id}`)
        return
      }
      if (viewable.length > 1) {
        navigate(`/profile/${row.user_id}?tournament=${tournamentId}`)
        return
      }
    } catch (err) {
      console.error('Error resolving leaderboard entry:', err)
    }
    navigate(`/profile/${row.user_id}`)
  }
  const [tournament, setTournament] = useState(null)
  const [brackets, setBrackets] = useState([])
  const [loading, setLoading] = useState(true)
  const [userEntries, setUserEntries] = useState({})
  const [activeTab, setActiveTab] = useState('brackets')
  // eslint-disable-next-line no-unused-vars
  const [userStats, setUserStats] = useState(null)
  const [leaderboard, setLeaderboard] = useState([])
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)

  useEffect(() => {
    fetchTournament()
  }, [tournamentId])

  useEffect(() => {
    if (targetUserId && brackets.length > 0) {
      fetchUserEntries()
      fetchUserStats()
    }
  }, [targetUserId, brackets])

  // Fetch leaderboard as soon as tournament+brackets are ready so the share
  // button in the tab row can render its card from any tab — not just after
  // the user visits the leaderboard tab.
  useEffect(() => {
    if (tournament && brackets.length > 0) {
      fetchLeaderboard()
    }
  }, [tournament, brackets])

  const fetchTournament = async () => {
    try {
      setLoading(true)
      const data = await fetchTournamentService({ tournamentId })

      if (data) {
        // Transform data to match expected format
        const transformedData = {
          id: data.id,
          name: data.name,
          title: data.name,
          sportId: data.sport?.id,
          sportName: data.sport?.name || 'Pickleball',
          startDate: data.startDate?.split('T')[0],
          endDate: data.endDate?.split('T')[0],
          submissionDeadlineFull: data.submissionDeadline, // Keep full UTC datetime
          status: data.status,
          createdBy: data.createdBy,
          divisions: (data.brackets || []).map(b => ({
            id: b.id,
            divisionId: b.divisionId,
            // Pool-play brackets have no division (DUB-116); fall back to a
            // stable "Pool Play" label so the card still has a heading.
            name: b.divisionName || (b.format === 'pool_then_sunday' ? 'Pool Play' : 'Bracket'),
            gender: b.divisionGender,
            teamSize: b.divisionTeamSize,
            matchCount: b.matchCount,
            playerCount: b.matchCount * 2, // Rough estimate
            entryCount: b.entryCount || 0,
            status: b.status || 'draft', // Include bracket status
            maxPoints: b.maxPoints || 0, // Include bracket's maximum possible points
            rounds: b.rounds || []
          }))
        }

        setTournament(transformedData)
        const inScope = groupBracketIds
          ? transformedData.divisions.filter(b => groupBracketIds.includes(b.id))
          : transformedData.divisions
        setBrackets([...inScope].sort(compareDivisions))
      }
    } catch (err) {
      console.error('Error fetching tournament:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserEntries = async () => {
    if (!targetUserId) return

    // Another user's profile: use the entries ProfileBody already loaded via the
    // gated RPC — a direct bracket_entries fetch would be RLS-blocked and leave
    // the Brackets tab empty.
    if (providedEntries) {
      const entriesMap = {}
      providedEntries.forEach(entry => {
        entriesMap[entry.bracket_id] = {
          entryId: entry.id,
          totalScore: entry.total_score || 0,
          potentialPoints: entry.potential_points || 0,
          accuracy: entry.accuracy || 0,
          championStatus: entry.champion_status,
          championName: entry.champion_team_name
        }
      })
      setUserEntries(entriesMap)
      return
    }

    try {
      const data = await fetchUserBracketEntries({
        userId: targetUserId,
        bracketIds: brackets.map(b => b.id)
      })

      const entriesMap = {}
      data?.forEach(entry => {
        entriesMap[entry.bracket_id] = {
          entryId: entry.id,
          totalScore: entry.total_score || 0,
          potentialPoints: entry.potential_points || 0,
          accuracy: entry.accuracy || 0,
          championStatus: entry.champion_status,
          championName: entry.champion_team_name
        }
      })
      setUserEntries(entriesMap)
    } catch (err) {
      console.error('Error fetching user entries:', err)
    }
  }

  const fetchUserStats = async () => {
    if (!targetUserId || !tournament) return
    // Skip the RLS-blocked stats fetch on another user's profile; the hero
    // (the only consumer) is hidden there anyway (showHero=false).
    if (providedEntries) return

    try {
      const data = await fetchUserBracketStats({
        userId: targetUserId,
        bracketIds: brackets.map(b => b.id)
      })

      const stats = {
        totalPoints: data?.reduce((sum, entry) => sum + (entry.total_score || 0), 0) || 0,
        maxPoints: data?.reduce((sum, entry) => sum + (entry.potential_points || 0), 0) || 0,
        championsPicked: data?.filter(entry => entry.champion_status !== 'not_selected').length || 0
      }

      setUserStats(stats)
    } catch (err) {
      console.error('Error fetching user stats:', err)
    }
  }

  const fetchLeaderboard = async () => {
    if (!tournament) return

    try {
      setLeaderboardLoading(true)
      // When tournament has a single bracket, pass its ID so the backend
      // returns champion_status labels ("Won"/"Active"/"Eliminated") instead
      // of the confusing ratio format ("1/1")
      const singleBracketId = brackets.length === 1 ? brackets[0].id : null
      const data = await fetchLeaderboardRankings({
        groupId,
        tournamentId: tournament.id,
        bracketId: singleBracketId,
      })
      setLeaderboard(data || [])
    } catch (err) {
      console.error('Error fetching leaderboard:', err)
      setLeaderboard([])
    } finally {
      setLeaderboardLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading tournament...</p>
      </div>
    )
  }

  if (!tournament) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Tournament not found</p>
      </div>
    )
  }

  return (
    <>
      {showHero && (
        <TournamentHero
          tournament={{
            name: tournament.name,
            sport_name: tournament.sportName,
            status: tournament.status,
            start_date: tournament.startDate,
            end_date: tournament.endDate,
            submission_deadline: tournament.submissionDeadlineFull,
            divisions: brackets
          }}
          bracketCount={brackets.length}
          entryCount={Object.keys(userEntries).length}
          totalBrackets={brackets.length}
        />
      )}

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <Card>
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between gap-2">
              <TabsList variant="secondaryNav" className="min-w-0">
                <TabsTrigger value="brackets" variant="secondaryNav">Brackets</TabsTrigger>
                <TabsTrigger value="leaderboard" variant="secondaryNav">Leaderboard</TabsTrigger>
                <TabsTrigger value="prizes" variant="secondaryNav">Prizes</TabsTrigger>
              </TabsList>
              {/* DUB-101: tournament-scoped leaderboard share. Sits inline
                  with the tab nav so it's reachable from any tab — the
                  share card is the leaderboard top 5 regardless of which
                  tab is active. */}
              {!leaderboardLoading && leaderboard.length > 0 && (
                <ShareButton
                  iconOnly
                  ariaLabel="Share tournament leaderboard"
                  title={tournament?.name || 'Dub tournament leaderboard'}
                  text="Check out this leaderboard on dub."
                  url={getShareUrl()}
                  className="h-10 w-10 p-0 rounded-full bg-background shrink-0"
                  variants={SHARE_CARD_VARIANTS}
                  getImage={(variantKey) => renderLeaderboardShareCard({
                    title: 'Who got the dub?',
                    subtitle: [tournament?.name, `${leaderboard.length.toLocaleString()} entries`].filter(Boolean).join(' • '),
                    variant: variantKey,
                    rows: leaderboard.slice(0, 5).map(r => ({
                      rank: r.rank,
                      full_name: r.full_name,
                      total_points: r.total_points,
                      is_current_user: r.is_current_user,
                      accuracy: r.avg_accuracy,
                      potential_points: r.potential_points,
                    })),
                  })}
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6 px-3 sm:px-6">
            {(() => {
              // Keep these as an array (NOT a Fragment): SwipeableTabsContent
              // keys its panels off each child's `props.value`, and
              // React.Children.toArray flattens an array but treats a Fragment
              // as one valueless child — which blanks every tab on the swipe
              // path. An array works for both the swipe and plain renders.
              const panels = [
                <TabsContent key="brackets" value="brackets" className="mt-0">
                  <TournamentBracketsTab
                    brackets={brackets}
                    userEntries={userEntries}
                    viewerIsOwner={viewerIsOwner}
                  />
                </TabsContent>,

                <TabsContent key="leaderboard" value="leaderboard" className="mt-0">
                  <LeaderboardTable
                    leaderboard={leaderboard}
                    loading={leaderboardLoading}
                    scope="tournament"
                    showChampionRatio={brackets.length <= 1}
                    singleBracket={brackets.length === 1}
                    emptyMessage="No rankings yet"
                    emptyDescription=""
                    onUserClick={handleLeaderboardUserClick}
                    champUserIds={champUserIds}
                  />
                </TabsContent>,

                <TabsContent key="prizes" value="prizes" className="mt-0">
                  <TournamentPrizes
                    tournamentId={tournament.id}
                    isAdmin={false}
                    groupId={groupId}
                    champNames={champNames}
                  />
                </TabsContent>,
              ]
              return swipeable ? (
                <SwipeableTabsContent tabs={['brackets', 'leaderboard', 'prizes']} activeTab={activeTab} onTabChange={setActiveTab}>
                  {panels}
                </SwipeableTabsContent>
              ) : panels
            })()}
          </CardContent>
        </Card>
      </Tabs>
    </>
  )
}
